/**
 * Services for OREME observatories
 * @package services
 * @author Julien Huon <julien.huon@umontpellier.fr>
 */

import { z } from "zod";

/**
 * Observatory interface representing an observatory in the OREME system
 * @interface Observatory
 * @property {string} id - The unique identifier of the observatory
 * @property {Object} name - The name of the observatory in French and English
 * @property {string} name.fr - The French name
 * @property {string} name.en - The English name
 * @property {Object} description - The description of the observatory in French and English
 * @property {string} description.fr - The French description
 * @property {string} description.en - The English description
 * @property {string} type - The type of the observatory
 * @property {string} theme - The theme of the observatory
 * @property {number | null} startYear - The starting year of the observatory
 * @property {boolean} isLabelized - Whether the observatory is labelized
 * @property {boolean} hasData - Whether the observatory has data
 * @property {string | null} portalURL - The data portal URL of the observatory
 * @property {string | null} corporateURL - The corporate website URL of the observatory
 * @property {string | null} catalogURL - The metadata catalog URL of the observatory
 */
interface Observatory {
  id: string;
  name: {
    fr: string;
    en: string;
  };
  description: {
    fr: string;
    en: string;
  };
  type: string;
  theme: string;
  startYear: number | null;
  isLabelized: boolean;
  hasData: boolean;
  portalURL: string | null;
  corporateURL: string | null;
  catalogURL: string | null;
}

/**
 * Zod schema for validating and transforming Observatory data from API response
 * @constant {z.ZodSchema<Observatory>}
 */
const schema = z
  .object({
    id: z.string(),
    name: z.object({
      fr: z.string(),
      en: z.string(),
    }),
    description: z.object({
      fr: z.string(),
      en: z.string(),
    }),
    obs_type: z.string(),
    theme: z.string(),
    start_year: z.number().nullable(),
    is_labelized: z.boolean(),
    has_data: z.boolean(),
    data_url: z.string().nullable(),
    oreme_url: z.string().nullable(),
    catalog_url: z.string().nullable(),
  })
  .transform((raw): Observatory => ({
    id: raw.id,
    name: {
      fr: raw.name.fr,
      en: raw.name.en,
    },
    description: {
      fr: raw.description.fr,
      en: raw.description.en,
    },
    type: raw.obs_type,
    theme: raw.theme,
    startYear: raw.start_year,
    isLabelized: raw.is_labelized,
    hasData: raw.has_data,
    portalURL: raw.data_url,
    corporateURL: raw.oreme_url,
    catalogURL: raw.catalog_url,
  }));

/**
 * Fetches observatories from the OREME API
 * @async
 * @function fetchObservatories
 * @returns {Promise<Observatory[]>} Array of Observatory objects (empty if none found)
 * @throws {Error} If the API request fails
 */
const fetchObservatories = async (): Promise<Observatory[]> => {
  const endpoint = `https://api.oreme.org/oreme/observatories`;

  const response = await fetch(endpoint, {
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch observatories: ${response.status} ${response.statusText}`,
    );
  }

  const json = await response.json();
  const items = json.items ?? [];

  const observatories = items.map((raw: unknown) => {
    const parsed = schema.safeParse(raw);
    return parsed.success ? parsed.data : null;
  });

  return observatories.filter(
    (observatory: Observatory | null): observatory is Observatory =>
      observatory !== null,
  );
};

export { type Observatory, fetchObservatories };
